import styled, { css } from 'styled-components'
import { colors, spacing, breakpoints, font } from '@chegg-ui/theme'
import { ButtonLink } from '@chegg/fanta'
import { SpecialOutline, Special } from '@chegg-wt/core/theme/fanta/button'
import CoreSaleBadge from '@chegg-wt/core/components/Modals/DiscountModal/SaleBadge/SaleBadge'
import { AspiraStack } from '@chegg-wt/core/theme/fanta/fonts'
import { Heading3 } from '@chegg-wt/core/theme/sunkist/typography'
import Icon from '../Icon'

export const Container = styled.div.attrs({
  role: 'region',
  'aria-label': 'Pricing Table',
})`
  ${AspiraStack};
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background-color: #f5f5f5;
  width: 100%;
  align-items: center;
`

export const Table = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 840px;
  padding: 16px 0;

  @media (max-width: 820px) {
    padding: 40px 4px 40px 4px;
  }
`

export const Title = styled.h1`
  font-size: 34px;
  text-align: center;
  margin-top: 0;
  margin-bottom: 32px;
  font-weight: 700;
  ${AspiraStack};
`

/**
 * This basically is just here to fix flexbox alignment issues
 */
export const Spacer = styled.div`
  flex: 1;
  border: 1px solid transparent;
  padding: 0 8px;
  @media (max-width: 820px) {
    padding: 2px;
  }
`

export const ProductName = styled.div<{ hasDiscountPercentage?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;

  flex: 1;
  font-weight: 700;
  font-size: 20px;
  text-align: center;
  align-items: center;
  position: relative;

  ${({ hasDiscountPercentage = false }) =>
    hasDiscountPercentage &&
    `
    @media (max-width: ${breakpoints.md.max}) {
      justify-content: end;
    }
  `}

  & + & {
    margin-left: 8px;

    @media (max-width: 820px) {
      margin-left: 0;
    }
  }

  @media (max-width: 820px) {
    font-size: 12px;
    padding: 0px 3px;
  }
`

export const ProductTitleFree = styled.span`
  @media (min-width: ${breakpoints.md.max}) {
    margin-right: 30px;
  }
`

export const ProductTitlePlus = styled.span`
  @media (min-width: ${breakpoints.md.max}) {
    margin-left: 34px;
  }
`

export const ProductNames = styled.div`
  display: flex;
  margin-bottom: 8px;
`

export const Products = styled.div`
  width: 100%;
`

export const FeatureWrapper = styled.div<{ recommended?: boolean }>`
  display: flex;
  flex-direction: column;

  flex: 1;
  padding: 0px 8px;

  background-color: white;
  font-size: 16px;
  font-weight: 500;

  border-left: 1px solid rgba(153, 153, 153, 0.5);
  border-right: 1px solid rgba(153, 153, 153, 0.5);

  & + & {
    margin-left: 8px;

    @media (max-width: 820px) {
      margin-left: 1px;
    }
  }

  *:first-child > & {
    border-top: 1px solid rgba(153, 153, 153, 0.5);
    border-radius: 8px 8px 0 0;

    ${({ recommended }) =>
      recommended &&
      `border-top: 2px solid rgba(153,153,153,0.8);
    position: relative;
    `};
  }

  *:last-child > &:first-child {
    border-bottom: 1px solid rgba(153, 153, 153, 0.5);
    border-radius: 0 0 8px 8px;
  }

  ${({ recommended }) =>
    recommended &&
    `
    background-color: #eaf8f8;
    border-left: 2px solid rgba(153,153,153,0.8);
    border-right: 2px solid rgba(153,153,153,0.8);
    @media (max-width: 820px){
      border-bottom: 2px solid rgba(153,153,153,0.8);
    }
  `} @media (max-width: 820px) {
    font-size: 12px;
    padding: 0px 4px;

    *:last-child > & {
      border-bottom: 1px solid rgba(153, 153, 153, 0.5);
      border-radius: 0 0 8px 8px;

      ${({ recommended }) =>
        recommended && 'border-bottom: 2px solid rgba(153,153,153,0.8);'};
    }
  }
`

export const FeatureRow = styled.div`
  display: flex;
  width: 100%;
`

export const Feature = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  flex: 1;
  padding: 16px 0;
  text-align: center;

  ${FeatureRow} & {
    border-bottom: 1px solid rgba(153, 153, 153, 0.5);
  }

  ${FeatureRow}:last-child ${FeatureWrapper}:first-child & {
    border-bottom: none;
  }

  @media (max-width: 820px) {
    ${FeatureRow}:last-child & {
      border-bottom: none;
    }
  }
`

export const Features = styled.div`
  width: 100%;
`

export const Check = styled(Icon)`
  width: 64px;
  color: rgb(34, 157, 158);

  @media (max-width: 820px) {
    width: 40px;
  }
`

export const PricesAndActions = styled.div`
  width: 100%;
`

export const OfferPrice = styled.div`
  font-weight: 700;
  font-size: 18px;
`

export const StrikedPrice = styled.s`
  font-weight: 400;
  font-size: 16px;
`

export const Price = styled.div<{ recommended?: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: white;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 20px;
  line-break: anywhere;
  line-height: 20px;

  flex: 1;
  padding: 16px 8px;
  margin-left: 8px;
  text-align: center;

  border-left: 1px solid rgba(153, 153, 153, 0.5);
  border-right: 1px solid rgba(153, 153, 153, 0.5);

  ${({ recommended }) =>
    recommended &&
    `
    background-color: #eaf8f8;
    border-left: 2px solid rgba(153,153,153,0.8);
    border-right: 2px solid rgba(153,153,153,0.8);
  `};

  @media (max-width: 820px) {
    font-size: 14px;
    background-color: inherit;
    border: none;
    padding: 8px 0;
    margin-left: 1px;
  }
`

export const Prices = styled.div`
  display: flex;
`

export const Action = styled.div<{ recommended?: boolean }>`
  display: flex;
  background-color: white;
  align-items: center;
  justify-content: center;

  flex: 1;

  padding: 8px;
  margin-left: 8px;

  border-left: 1px solid rgba(153, 153, 153, 0.5);
  border-right: 1px solid rgba(153, 153, 153, 0.5);
  border-bottom: 1px solid rgba(153, 153, 153, 0.5);
  border-radius: 0 0 8px 8px;

  ${({ recommended }) =>
    recommended &&
    `
  background-color: #eaf8f8;
  border-left: 2px solid rgba(153,153,153,0.8);
  border-right: 2px solid rgba(153,153,153,0.8);
  border-bottom: 2px solid rgba(153,153,153,0.8);
  `} @media (max-width: 820px) {
    background-color: inherit;
    border: none;
    margin-left: 1px;
    padding: 2px;
  }
`

export const Actions = styled.div`
  display: flex;
`

const SignUpCss = css`
  margin-bottom: ${spacing.px16};
  width: 100%;
`

export const Link = styled(ButtonLink).attrs({ size: 'medium' })`
  ${SpecialOutline}
  ${SignUpCss}
`

export const LinkRecommended = styled(ButtonLink).attrs({ size: 'medium' })`
  ${Special}
  ${SignUpCss}
`

export const Footer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 16px;
`

export const TermsAndConditions = styled.a`
  font-size: 14px;
  text-align: center;
  text-decoration: none;
  font-weight: 700;
  color: ${colors.link};
`

export const SaleBadge = styled(CoreSaleBadge)`
  font-size: ${font.size.px18};
  position: relative;

  &.discountSaleBadge {
    > div {
      top: 15px;
      left: 5px;
      padding: 0;
    }
  }
`
export const DiscountBadgeText = styled.div`
  ${Heading3};
  color: ${colors.white};
  font-weight: 700;
  text-align: center;
`

export const DiscountBadgeWrapper = styled.div`
  @media (min-width: ${breakpoints.md.max}) {
    position: absolute;
    bottom: -25px;
    left: -35px;
    z-index: 1;
  }
`
